import { useEffect, useState } from "react";
import { Box, Typography, Button, Stack, TextField, FormControl, Select, MenuItem, InputLabel, Pagination, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";

import { getAllHotels, insertHotel, updateHotel, deleteHotel } from "../../api/hotelsAPI";
import { setAllHotels } from "../../reduxcomponents/slices/hotelsSlice";
import useSnackbar from "../../hooks/useSnackbar";
import usePermissions from "../../hooks/UsePermissions";

import HotelsList from "./HotelsList";
import HotelDrawer from "./HotelDrawer";

const emptyPrice = Object.fromEntries(
  [
    "cp_plan", "ep_plan", "ap_plan", "map_plan",
    "extra_mat_cp", "extra_mat_ap", "extra_mat_ep", "extra_mat_map",
    "cnb_cp", "cnb_ap", "cnb_ep", "cnb_map"
  ].map((k) => [k, 0])
);

const emptyHotel = {
  destination: "",
  sub_destination: "",
  hotel_name: "",
  type: "Deluxe",
  isActive: true,
  category: [
    {
      room_cat: "",
      season_price: { ...emptyPrice },
      off_season_price: { ...emptyPrice },
    },
  ],
  rating: 0,
  contacts: [""],
  address: "",
  hotel_link: "",
  amenities: [""],
  notes: [""],
  season_dates: [],
};

const HotelsPage = () => {
  const [hotels, setHotels] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("view");
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState(emptyHotel);
  const [viewMode, setViewMode] = useState("grid");

  const [searchQuery, setSearchQuery] = useState("");
  const [destination, setDestination] = useState("");
  const [subDestination, setSubDestination] = useState("");
  const [type, setType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [toggleLoading, setToggleLoading] = useState({});

  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const getPermission = usePermissions();
  const configData = useSelector((state) => state.config.configData || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const permissions = {
    edit: getPermission("hotel", "alter"),
    delete: getPermission("hotel", "delete"),
    add: getPermission("hotel", "add-hotel"),
  };

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const size = 12;

  const [allDestinations, setAllDestinations] = useState([]);
  const [allSubDestinations, setAllSubDestinations] = useState([]);

  // Fetch unique destinations once on mount
  useEffect(() => {
    (async () => {
      try {
        const list = await getAllHotels(); // unpaginated to get all options
        if (Array.isArray(list)) {
          setAllDestinations([...new Set(list.map(h => h.destination).filter(Boolean))]);
          setAllSubDestinations([...new Set(list.map(h => h.sub_destination).filter(Boolean))]);
        }
      } catch (err) {
        console.error("Error fetching options", err);
      }
    })();
  }, []);

  // Fetch paginated hotels
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const res = await getAllHotels({
          page,
          size,
          searchQuery,
          destination,
          subDestination,
          type,
          isActive: statusFilter !== "" ? statusFilter : undefined
        });
        
        if (res.data) {
           setHotels(res.data);
           setTotalPages(res.totalPages || 1);
           dispatch(setAllHotels(res.data)); // Update Redux with current page
        } else {
           // fallback if backend didn't paginate
           setHotels(res);
           dispatch(setAllHotels(res));
        }
      } catch (err) {
        console.error(err);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchHotels();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [dispatch, page, size, searchQuery, destination, subDestination, type, statusFilter]);

  /** HANDLERS **/
  const handleView = (hotel) => {
    setDrawerMode("view");
    setSelectedHotel(hotel);
    setDrawerOpen(true);
  };

  const handleEdit = (hotel) => {
    setDrawerMode("edit");
    const editedHotel = JSON.parse(JSON.stringify(hotel));
    if (editedHotel.isActive === undefined) {
      editedHotel.isActive = true;
    }
    if (editedHotel.season_dates) {
      editedHotel.season_dates = editedHotel.season_dates.map(sd => {
        let startStr = sd.start || '';
        let endStr = sd.end || '';
        
        const parseLegacyDate = (dateStr) => {
          if (!dateStr || dateStr.length <= 5) return dateStr; // Already MM-dd or empty
          const d = new Date(dateStr);
          if (!isNaN(d)) {
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${month}-${day}`;
          }
          return dateStr;
        };

        return { 
          start: parseLegacyDate(startStr), 
          end: parseLegacyDate(endStr) 
        };
      });
    }
    setFormInitialValues(editedHotel);
    setSelectedHotel(hotel);
    setDrawerOpen(true);
  };

  const handleAdd = () => {
    setDrawerMode("add");
    setFormInitialValues(JSON.parse(JSON.stringify(emptyHotel)));
    setDrawerOpen(true);
  };

  const handleToggleActive = async (hotel, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const nextStatus = hotel.isActive === false ? true : false;
    setToggleLoading((prev) => ({ ...prev, [hotel._id]: true }));

    try {
      await updateHotel(hotel._id, { ...hotel, isActive: nextStatus });
      const updatedHotels = hotels.map((h) =>
        h._id === hotel._id ? { ...h, isActive: nextStatus } : h
      );
      setHotels(updatedHotels);
      dispatch(setAllHotels(updatedHotels));
      showSnackbar(`Hotel "${hotel.hotel_name}" marked as ${nextStatus ? "Active" : "Inactive"}`, "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Error updating hotel status", "error");
    } finally {
      setToggleLoading((prev) => ({ ...prev, [hotel._id]: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await deleteHotel(id);
      const updatedHotels = hotels.filter((h) => h._id !== id);
      setHotels(updatedHotels);
      dispatch(setAllHotels(updatedHotels)); // Update Redux
      showSnackbar("Hotel deleted", "success");
    } catch {
      showSnackbar("Error deleting hotel", "error");
    }
  };

  const handleSubmit = async (values) => {
    try {
      let updatedHotels;
      if (drawerMode === "edit") {
        // A changed link needs to be re-approved on the Hotel Link Review page
        if ((values.hotel_link || "") !== (selectedHotel.hotel_link || "")) {
          values = { ...values, hotel_link_approved: false, hotel_link_approved_at: null };
        }
        await updateHotel(selectedHotel._id, values);
        updatedHotels = hotels.map((h) => (h._id === selectedHotel._id ? { ...values, _id: h._id } : h));
        setHotels(updatedHotels);
        dispatch(setAllHotels(updatedHotels)); // Update Redux
        showSnackbar("Hotel updated", "success");
      } else {
        const res = await insertHotel(values);
        updatedHotels = [res.data, ...hotels];
        setHotels(updatedHotels);
        dispatch(setAllHotels(updatedHotels)); // Update Redux
        showSnackbar("Hotel added", "success");
      }

      setDrawerOpen(false);
    } catch (err) {
      console.error(err);
      showSnackbar("Error saving hotel", "error");
    }
  };

  const handleBatchSave = async (hotelsToSave) => {
    try {
      const savedList = [];
      for (const h of hotelsToSave) {
        const hotelPayload = {
          ...h,
          type: (h.type && h.type !== "Hotel") ? h.type : "Deluxe"
        };
        const res = await insertHotel(hotelPayload);
        if (res?.data) savedList.push(res.data);
      }
      const updatedHotels = [...savedList, ...hotels];
      setHotels(updatedHotels);
      dispatch(setAllHotels(updatedHotels));
      showSnackbar(`Successfully added ${savedList.length} hotels!`, "success");
      setDrawerOpen(false);
    } catch (err) {
      console.error(err);
      const errMsg = err?.response?.data?.message || err?.message || "Error saving hotels in batch";
      showSnackbar(errMsg, "error");
    }
  };

  return (
    <Box p={3} sx={{ background: "#f5f6fa", minHeight: "100vh" }}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight={600}>Hotel Management</Typography>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => navigate("/hotels/link-review")}>
            Review Hotel Links
          </Button>
          {permissions.add && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              Add Hotel
            </Button>
          )}
        </Stack>
      </Box>

      {/* Filters */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          label="Search by Hotel Name"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Destination</InputLabel>
          <Select value={destination} onChange={(e) => { setDestination(e.target.value); setPage(1); }} label="Destination">
            <MenuItem value="">All</MenuItem>
            {allDestinations.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sub Destination</InputLabel>
          <Select value={subDestination} onChange={(e) => { setSubDestination(e.target.value); setPage(1); }} label="Sub Destination">
            <MenuItem value="">All</MenuItem>
            {allSubDestinations.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} label="Type">
            <MenuItem value="">All</MenuItem>
            {configData?.additionalCosts?.hotel?.map((hotelType) => (
              <MenuItem key={hotelType.type} value={hotelType.type}>
                {hotelType.type}
              </MenuItem>
            )) || [...new Set(hotels.map((h) => h.type))].map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            label="Status"
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="true">Active Only</MenuItem>
            <MenuItem value="false">Inactive Only</MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => { if (newMode !== null) setViewMode(newMode); }}
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModuleIcon />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Hotel Cards / List */}
      <HotelsList
        hotels={hotels}
        viewMode={viewMode}
        searchQuery=""
        destination=""
        subDestination=""
        type=""
        permissions={permissions}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
        toggleLoading={toggleLoading}
      />

      <Box display="flex" justifyContent="center" mt={3} mb={3}>
        <Pagination 
           count={totalPages} 
           page={page} 
           onChange={(e, value) => setPage(value)} 
           color="primary" 
        />
      </Box>

      {/* Drawer */}
      <HotelDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        mode={drawerMode}
        selectedHotel={selectedHotel}
        formInitialValues={formInitialValues}
        onSubmit={handleSubmit}
        onBatchSave={handleBatchSave}
      />

      <SnackbarComponent />
    </Box>
  );
};

export default HotelsPage;
